import type { BarcodeResult, DocumentResult } from '../shared/desktop-api';
import type { LineItemCell } from '../shared/line-item-evidence';
import type {
  FieldDraft,
  FieldDraftCommand,
  FieldDraftIdentity,
  FieldDraftOutcome,
} from './field-drafts';
import type { LineDraftEvent } from './line-item-drafts';

export const reviewWorkspaceChannel = 'capturepoint-review-workspace-v1';

export type ReviewFieldConfig = {
  id: string;
  name: string;
  kind: string;
  required: boolean;
};

export type ReviewDocumentNavigation = {
  id: string;
  mutationToken: string;
  sourceName: string;
  documentType: string;
  needsReview: boolean;
  needsTeaching?: boolean;
  pages: Array<{
    number: number;
    previewPath: string;
    startsNewDocument?: boolean;
    boundaryConfidence?: number;
    boundaryReasons?: string[];
  }>;
};

export type ReviewWorkspaceSnapshot = {
  jobId?: string;
  jobName: string;
  /** The paperclip's words, as the main window says them (the job's start check decides the second half). */
  joinTooltip?: string;
  documentIndex: number;
  documentCount: number;
  pageIndex: number;
  pageCount: number;
  document: DocumentResult;
  /** Stable for exactly the document state rendered in this snapshot. */
  documentMutationToken: string;
  documents: ReviewDocumentNavigation[];
  fields: ReviewFieldConfig[];
  documentTypes: Array<{ id: string; name: string }>;
  review?: {
    documentTypeId?: string;
    canConfirmDocument: boolean;
    fullyConfirmed: boolean;
    /**
     * The one main button as the main window shows it (single confirm, 2026-10-05): "Confirm &
     * next", "Next" on a document already confirmed, "Confirm" on the last of the list, or
     * "Document confirmed" with nowhere to go. The panel shows the same words and sends
     * confirm-document; the main window decides what it does.
     */
    mainAction?: {
      kind: 'confirm-next' | 'confirm' | 'next' | 'confirmed';
      label: string;
      busy?: boolean;
    };
    /** Skip is offered (Needs review and Teach, on a document still to confirm). */
    skip?: boolean;
    /** "Done ✓" is showing for the document just confirmed. */
    confirmedBeat?: boolean;
    /** "✓ Confirmed · Back" is offered for the document just confirmed. */
    backOffer?: boolean;
    /** The list being worked is finished (All caught up): no main button. */
    done?: boolean;
    /** The things to check, in the engine's order; a bare cue is not one (review-wait-reasons.ts). */
    issues: Array<{
      label: string;
      fieldName?: string;
      boundary?: boolean;
      /** Where it is answered ("Bill To", "Line 45"): the to-check list's name for it. */ place?: string;
    }>;
    /** Why the document waits, in the engine's words (whyDocumentWaits): the line above the main window's fields. */
    waiting?: string;
    /** Why it is here when no place on the screen answers it (waitingNote), said under "N to check". */
    note?: string;
    learningStatus?: string;
    learningReceipt?: string;
    /** Delete is offered on this document (ADR-0096: Manual only), and whether it is unavailable for now. */
    canDelete?: boolean;
    deleteBusy?: boolean;
    /** "Deleted an Invoice": the main window's notice after a Delete while its Undo shows, for the panel to show too. */
    deletedNotice?: string;
  };
  activeEvidenceLines: number[];
  /** The value behind activeEvidenceLines, so only its words are outlined (field-evidence-words.ts). */
  activeEvidenceCitation?: { value: string; words: number[] | null } | null;
  /** Presentation state owned by the primary review workspace. */
  pinnedFieldIds?: string[];
  hoveredFieldId?: string | null;
  activeBarcodeEvidence?: BarcodeResult | null;
  activeLineItemRow?: number | null;
  selectedFieldId?: string | null;
  imageSelectionSerial?: number;
  imageSelectedCell?: LineItemCell | null;
  busyMessage?: string | null;
  /**
   * Splits, joins and undos the main window has accepted and not yet saved, by
   * the batch page they are about. The panel draws "Saving…" on that boundary
   * instead of its control; its other controls stay usable.
   */
  pendingBoundaries?: Array<{
    kind: 'split' | 'join' | 'undo';
    pageNumber: number;
    phase: 'waiting' | 'saving';
  }>;
  /**
   * Whether a line-item write is in flight in the main window.
   *
   * Docked, the controls in a row disable while one saves. Detached, the panel
   * had no way to know and passed `false` always — so its buttons stayed live,
   * and the main window's handler drops an edit that arrives mid-save. A click
   * that lands in that window does nothing and says nothing, which reads as the
   * panel being broken rather than busy.
   */
  lineSaving?: boolean;
  /** Shared whole-document save lane for detached scalar field controls. */
  fieldSaving?: boolean;
  /** The line the main button refused to confirm over; a detached table flashes it too. */
  refusedLineRow?: { rowNumber: number; serial: number } | null;
  fieldDrafts?: readonly FieldDraft[];
  lineDrafts?: readonly FieldDraft[];
};

/**
 * A detached panel is another renderer and its message can arrive after the main
 * window has navigated or saved. Bind every mutating gesture to the exact
 * document state the operator saw, without serializing the much larger OCR word
 * arrays that never change during review.
 */
export const reviewDocumentMutationToken = (document: DocumentResult) =>
  JSON.stringify({
    id: document.id,
    documentType: document.documentType,
    documentTypeId: document.documentTypeId,
    documentTypeSchemaVersion: document.documentTypeSchemaVersion,
    documentTypeVerified: document.documentTypeVerified ?? false,
    confirmedAsWhole: document.confirmedAsWhole ?? false,
    // The background document-index reconciliation uses this same token to prove
    // its opening flag is still current. A review recheck can durably change only
    // this bit; omitting it let an older index response paint the stale flag back.
    needsReview: document.needsReview,
    needsTeaching: document.needsTeaching ?? false,
    pages: document.pages.map((page) => [
      page.number,
      page.previewPath,
      page.sourcePath ?? null,
      page.sourcePageNumber ?? null,
      // Two acquisitions may legitimately share path and page number. This is the
      // stable identity of the physical import the operator is actually viewing.
      page.sourceOccurrenceId ?? null,
      page.startsNewDocument ?? false,
      page.boundaryConfidence,
      page.boundaryReasons ?? [],
    ]),
    fields: document.fields,
    lineItems: document.lineItems,
  });

/**
 * The revisions each document moved through by its own typing alone: every draft save's optimistic
 * paint and its saved result, as the main window applied them (mutation tokens). A detached panel's
 * confirmation is sent at once, bound to the revision on its screen, right after the typing it
 * submitted; the main window may already show that typing when the press arrives. A press bound to
 * a revision that only this typing has moved on from is the press the operator made; any other
 * change in between (a re-read, another edit, a type change) is a stale press and is refused.
 */
export class OwnTypingRevisions {
  private steps = new Map<string, Array<readonly [string, string]>>();
  static readonly LIMIT = 64;
  record(documentId: string, from: string, to: string) {
    if (from === to) return;
    const steps = this.steps.get(documentId) ?? [];
    steps.push([from, to]);
    if (steps.length > OwnTypingRevisions.LIMIT) steps.shift();
    this.steps.set(documentId, steps);
  }
  /** Whether `to` is `from`, or is reached from it through this document's own typing only. */
  reaches(documentId: string, from: string, to: string) {
    if (from === to) return true;
    const steps = this.steps.get(documentId) ?? [];
    const seen = new Set([from]);
    const queue = [from];
    while (queue.length) {
      const at = queue.shift()!;
      for (const [start, end] of steps) {
        if (start !== at || seen.has(end)) continue;
        if (end === to) return true;
        seen.add(end);
        queue.push(end);
      }
    }
    return false;
  }
}

type DocumentBoundAction = { documentId: string; documentMutationToken: string };

export type ReviewWorkspaceAction =
  | { type: 'select-document'; index: number; pageIndex?: number }
  | { type: 'select-page'; index: number }
  | ({ type: 'split-document'; pageNumber: number } & DocumentBoundAction)
  | ({
      type: 'merge-document';
      previousDocumentId: string;
      previousDocumentMutationToken: string;
    } & DocumentBoundAction)
  | { type: 'focus-field'; documentId: string; name: string }
  | ({ type: 'toggle-field-evidence'; fieldId: string } & DocumentBoundAction)
  | ({ type: 'hover-field-evidence'; fieldId: string | null } & DocumentBoundAction)
  | ({ type: 'set-document-type'; documentTypeId: string } & DocumentBoundAction)
  /** The one main button: confirm & next, next, or confirm, as the main window decides. */
  | ({ type: 'confirm-document' } & DocumentBoundAction)
  /** Delete (ADR-0096), at once from the panel; the main window deletes it. */
  | ({ type: 'delete-document' } & DocumentBoundAction)
  /** The Undo of the panel's "Deleted an Invoice · Undo": the main window restores it, while its own Undo shows. */
  | { type: 'undo-delete' }
  | ({ type: 'focus-review'; index: number; label: string } & DocumentBoundAction)
  | { type: 'skip-document' }
  | { type: 'back-to-confirmed' }
  /** Ctrl+Z in a moved panel: the main window undoes the last change, if its undo still shows (last-change.ts). */
  | {
      type: 'undo';
      /** Pressed in a field's box with no typing of its own: only a saved value is taken back (text-undo.ts). */ fromTextBox?: boolean;
    }
  | ({
      type: 'set-field';
      name: string;
      value: string;
      pageNumber: number;
      lineIndexes?: number[];
      /** Exact symbol selected in the viewer, when this is a barcode gesture. */
      barcodeEvidence?: BarcodeResult | null;
    } & DocumentBoundAction)
  /** Settle a lookup tie with one of the rows the list offered. */
  | ({ type: 'pick-lookup-choice'; name: string; rowNumber: number } & DocumentBoundAction)
  | { type: 'focus-line-item'; documentId: string; rowNumber: number; cell: LineItemCell | 'row' }
  | ({
      type: 'set-line-item';
      rowNumber: number;
      patch: {
        description?: string;
        quantity?: number | null;
        unitPrice?: number | null;
        amount?: number | null;
      };
    } & DocumentBoundAction)
  /** "Looks right" on a row whose arithmetic disagrees — the operator vouching for the page as printed. */
  | ({ type: 'accept-line-item'; rowNumber: number; requestId?: string } & DocumentBoundAction)
  | ({ type: 'add-line-item'; pageNumber: number } & DocumentBoundAction)
  | ({ type: 'delete-line-item'; rowNumber: number } & DocumentBoundAction);

export type ReviewWorkspaceMessage =
  | { type: 'row-confirmation-result'; requestId: string; documentMutationToken: string | null }
  | { type: 'close-permission-barrier'; requestId: string; participantId: number }
  | LineDraftEvent
  | { type: 'line-draft-command'; command: FieldDraftCommand }
  | { type: 'request-state' }
  | { type: 'state'; snapshot: ReviewWorkspaceSnapshot | null }
  /** The hovered field alone: hovering changes nothing else, so it does not resend the whole state. */
  | { type: 'hover'; fieldId: string | null }
  | { type: 'action'; action: ReviewWorkspaceAction }
  | { type: 'field-draft-command'; command: FieldDraftCommand }
  | { type: 'field-draft-sync'; draft: FieldDraft }
  | { type: 'field-draft-result'; draft: FieldDraftIdentity; outcome: FieldDraftOutcome }
  | { type: 'field-draft-discarded'; draft: FieldDraftIdentity };

/**
 * Whether a field draft still applies to the document as it is now. The exact state the person saw
 * (its mutation token) always does. So does a document whose re-read changed nothing the draft rests
 * on: the same document, type and confirmation, and this field still holding the value the typing
 * began from. A re-read landing during a save (the open document's own load, finishing late under
 * load) left the token different with the same content, and the typing ended "Not saved" with no
 * write sent (Paige's loaded review tests, 2026-10-10).
 */
export function fieldDraftStillApplies(
  document: DocumentResult,
  draft: { documentMutationToken: string; fieldId: string; name?: string; baseValue?: string },
): boolean {
  if (reviewDocumentMutationToken(document) === draft.documentMutationToken) return true;
  if (draft.baseValue === undefined || draft.name === undefined) return false;
  let seen: {
    id?: string;
    documentType?: string;
    documentTypeId?: string;
    documentTypeSchemaVersion?: number;
    confirmedAsWhole?: boolean;
  };
  try {
    seen = JSON.parse(draft.documentMutationToken);
  } catch {
    return false;
  }
  if (
    seen.id !== document.id ||
    seen.documentType !== document.documentType ||
    (seen.documentTypeId ?? null) !== (document.documentTypeId ?? null) ||
    (seen.documentTypeSchemaVersion ?? null) !== (document.documentTypeSchemaVersion ?? null) ||
    (seen.confirmedAsWhole ?? false) !== (document.confirmedAsWhole ?? false)
  )
    return false;
  const field = document.fields.find((item) =>
    item.fieldId
      ? item.fieldId === draft.fieldId && item.name === draft.name
      : item.name === draft.name,
  );
  return (field?.value ?? '') === draft.baseValue;
}
